import { useState, useEffect, useCallback } from 'react';
import { UserCheck, UserX, Loader2, Clock, Mail, Phone, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getCountryByCode } from '../../utils/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPendingUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/pending`, { headers });
      setUsers(data.users || []);
    } catch (e) {
      toast.error('Errore caricamento utenti in attesa');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleAction = async (email, action) => {
    const confirmMsg = action === 'approve'
      ? `Approvare l'account di ${email}? L'utente riceverà una email di conferma e potrà accedere.`
      : `Rifiutare e ELIMINARE l'account di ${email}? Riceverà una email con il motivo. Questa azione non è reversibile.`;
    if (!window.confirm(confirmMsg)) return;

    let reason = null;
    if (action === 'reject') {
      reason = window.prompt('Motivo del rifiuto (verrà incluso nell\'email):', 'Profilo non conforme ai requisiti della piattaforma.');
      if (reason === null) return; // cancelled
    }

    setProcessing(email);
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${encodeURIComponent(email)}/approval`,
        { action, reason },
        { headers }
      );
      toast.success(data.message + (data.email_sent ? ' · Email inviata' : ''));
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-pending-users">
      <div className="flex items-center gap-3">
        <Clock size={16} className="text-[#FF9F0A]" />
        <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Registrazioni in attesa di approvazione</span>
        <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">
          {users.length} in attesa
        </span>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin inline text-[#4A6B8A]" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 border border-[#B8D4E8] bg-white">
          <UserCheck size={48} className="mx-auto text-[#32D74B] mb-3" />
          <p className="text-sm text-[#4A6B8A]">Nessuna richiesta di approvazione pendente</p>
          <p className="text-xs text-[#6B8AB5] mt-1">Ogni nuova registrazione apparirà qui in attesa di approvazione.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {users.map((u) => (
            <div key={u.email} className="border border-[#FF9F0A]/30 bg-white p-4" data-testid={`pending-card-${u.email}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-sm font-bold text-[#0D1B2A]">{u.nome} {u.cognome}</div>
                  <div className="flex items-center gap-1 text-xs text-[#4A6B8A] mt-1">
                    <Mail size={11} /> {u.email}
                  </div>
                  {u.telefono && (
                    <div className="flex items-center gap-1 text-xs text-[#4A6B8A] mt-0.5">
                      <Phone size={11} /> {u.telefono}
                    </div>
                  )}
                  <div className="flex items-center gap-1 text-[10px] font-mono text-[#6B8AB5] mt-1">
                    <FileText size={10} />
                    {u.numero_passaporto
                      ? (() => {
                          const c = getCountryByCode(u.paese_passaporto);
                          return <span>{c ? c.flag : '📔'} Passaporto {c ? c.name : ''}: {u.numero_passaporto}</span>;
                        })()
                      : u.codice_iss
                        ? <span>🇸🇲 ISS: {u.codice_iss}</span>
                        : (u.codice_fiscale || 'N/A')}
                  </div>
                </div>
                <span className="text-[9px] uppercase tracking-widest bg-[#FF9F0A]/10 text-[#FF9F0A] px-2 py-1 border border-[#FF9F0A]/30 font-bold">
                  <Clock size={10} className="inline mr-1" /> Attesa
                </span>
              </div>
              <div className="text-[10px] text-[#B8D4E8] uppercase tracking-widest mb-3">
                Registrato: {new Date(u.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleAction(u.email, 'approve')}
                  disabled={processing === u.email}
                  className="flex items-center justify-center gap-1 py-2 bg-[#32D74B] text-white text-[11px] font-bold uppercase tracking-widest hover:bg-[#28B840] disabled:opacity-40"
                  data-testid={`approve-user-${u.email}`}
                >
                  {processing === u.email ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
                  Approva
                </button>
                <button
                  onClick={() => handleAction(u.email, 'reject')}
                  disabled={processing === u.email}
                  className="flex items-center justify-center gap-1 py-2 bg-[#FF3B30] text-white text-[11px] font-bold uppercase tracking-widest hover:bg-[#FF1A1A] disabled:opacity-40"
                  data-testid={`reject-user-${u.email}`}
                >
                  <UserX size={12} /> Rifiuta
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
